import React from 'react';
import { CheckCircle2, Zap, AlertTriangle, History } from 'lucide-react';

/**
 * Tiny pill badge near the Refresh button.
 * Reads the latest refresh event (latestPriceChange) and shows a color-coded
 * indicator + tooltip about data-source health.
 * Click opens Refresh History modal.
 */
const DataSourceHealthBadge = ({ latestPriceChange, onOpenHistory }) => {
  if (!latestPriceChange) return null;

  const health = latestPriceChange.data_source_health || 'healthy';
  const stocksRefreshed = latestPriceChange.stocks_refreshed;
  const stocksAttempted = latestPriceChange.stocks_attempted;
  const lastRefresh = latestPriceChange.last_refresh;

  const relativeTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = Date.now();
    const diff = Math.max(0, Math.round((now - d.getTime()) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  };

  const styles = {
    healthy: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
      Icon: CheckCircle2,
      label: 'Fresh',
      tooltip: `Data source healthy • ${stocksRefreshed}/${stocksAttempted} stocks • Last refresh ${relativeTime(lastRefresh)}`,
    },
    slow: {
      cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
      Icon: Zap,
      label: 'Source slow',
      tooltip: `Yahoo Finance is being slow today • only ${stocksRefreshed}/${stocksAttempted} stocks refreshed • Click to see refresh history`,
    },
    degraded: {
      cls: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
      Icon: AlertTriangle,
      label: 'Source degraded',
      tooltip: `Yahoo Finance is having issues • only ${stocksRefreshed}/${stocksAttempted} stocks refreshed • Some prices may be stale`,
    },
  };

  const { cls, Icon, label, tooltip } = styles[health] || styles.healthy;

  return (
    <button
      onClick={onOpenHistory}
      title={tooltip}
      data-testid="data-source-health-badge"
      className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${cls}`}
    >
      <Icon size={13} />
      <span>{label}</span>
      {stocksRefreshed !== undefined && stocksAttempted !== undefined && stocksAttempted > 0 && (
        <span className="text-[11px] opacity-80">
          {stocksRefreshed}/{stocksAttempted}
        </span>
      )}
      <History size={12} className="opacity-60 ml-0.5" />
    </button>
  );
};

export default DataSourceHealthBadge;
